/**
 * Survey scheduling, overdue buckets and reminders (ADR-0014, ADR-0020).
 * One definition of "days to follow-up" for app, emails and Domo:
 *   positive = days left until due, negative = days overdue.
 */
import { addDays, addMonths, daysBetween, type IsoDate } from '@sss/core';

export interface SiteSchedule {
  enrolled: boolean;
  active: boolean;
  frequencyMonths: number;
  contractStart: IsoDate | null;
  /** On-site date of the last completed survey of this type. */
  lastSurveyDate: IsoDate | null;
  /**
   * Explicit due date. Before the first survey it is the initial (staggered or
   * imported) date and replaces the contract-start rule. After a survey it is an
   * unscheduled survey and only wins when earlier than the regular date.
   */
  overrideDueDate: IsoDate | null;
}

/** Next due date, or null when the site is not tracked. */
export function nextDueDate(s: SiteSchedule, firstSurveyDueDays: number): IsoDate | null {
  if (!s.enrolled || !s.active) return null;
  if (!s.lastSurveyDate) {
    if (s.overrideDueDate) return s.overrideDueDate;
    return s.contractStart ? addDays(s.contractStart, firstSurveyDueDays) : null;
  }
  const regular = addMonths(s.lastSurveyDate, s.frequencyMonths);
  return s.overrideDueDate && s.overrideDueDate < regular ? s.overrideDueDate : regular;
}

export function daysToFollowUp(due: IsoDate, today: IsoDate): number {
  return daysBetween(today, due);
}

export const FOLLOW_UP_BUCKETS = [
  'OVERDUE_90_PLUS',
  'OVERDUE_90',
  'OVERDUE_60',
  'OVERDUE_30',
  'DUE_30',
  'DUE_60',
  'DUE_90',
  'NOT_DUE',
] as const;
export type FollowUpBucket = (typeof FOLLOW_UP_BUCKETS)[number];

/** Buckets from the Survey Overdue mockup: "Close to due 30/60/90 D", "Overdue 30/60/90/90+ D". */
export function followUpBucket(days: number): FollowUpBucket {
  if (days < -90) return 'OVERDUE_90_PLUS';
  if (days < -60) return 'OVERDUE_90';
  if (days < -30) return 'OVERDUE_60';
  if (days < 0) return 'OVERDUE_30';
  if (days <= 30) return 'DUE_30';
  if (days <= 60) return 'DUE_60';
  if (days <= 90) return 'DUE_90';
  return 'NOT_DUE';
}

export interface EnrolmentCandidate {
  siteId: string;
  /** Higher first: armed post, high asset value, priority flag. */
  priority: number;
  /** Explicit initial due date from the CSV, if given. */
  initialDueDate?: IsoDate | null;
}

/**
 * Spread first due dates across one cycle so nothing starts overdue
 * (ADR-0020). Sites with an explicit date keep it; the rest are spaced
 * evenly from `start`, highest priority first.
 */
export function staggerInitialDueDates(
  sites: EnrolmentCandidate[],
  start: IsoDate,
  cycleMonths = 12,
): Map<string, IsoDate> {
  const out = new Map<string, IsoDate>();
  const rest: EnrolmentCandidate[] = [];
  for (const s of sites) {
    if (s.initialDueDate) out.set(s.siteId, s.initialDueDate);
    else rest.push(s);
  }
  rest.sort((a, b) => b.priority - a.priority || a.siteId.localeCompare(b.siteId));
  const cycleDays = daysBetween(start, addMonths(start, cycleMonths));
  rest.forEach((s, i) => {
    const offset = rest.length === 1 ? 0 : Math.floor((i * cycleDays) / rest.length);
    out.set(s.siteId, addDays(start, offset));
  });
  return out;
}

export interface FirstDueCandidate {
  siteId: string;
  priority: number;
  contractStart: IsoDate | null;
  /** The site's resolved `firstSurveyDueDays` (settings cascade). */
  firstSurveyDueDays: number;
  /** Already has a last survey date or an explicit initial due date: nothing to plan. */
  scheduled: boolean;
}

/**
 * Initial due dates to store when sites are enrolled (ADR-0014, ADR-0020, ADR-0027).
 * Sites missing from the result need nothing stored:
 *  - sites with history or an explicit date keep it;
 *  - a contract start whose first-survey date (start + firstSurveyDueDays) is
 *    still ahead applies as is, through `nextDueDate`.
 * The rest have no contract start, or one so old its first-survey date has
 * passed (an established contract at go-live, which must not start overdue):
 *  - one such site alone gets enrolment date + firstSurveyDueDays;
 *  - several (a bulk go-live) are spread across one cycle, priority first.
 */
export function planFirstDueDates(sites: FirstDueCandidate[], start: IsoDate, cycleMonths = 12): Map<string, IsoDate> {
  const unplanned = sites.filter(
    (s) => !s.scheduled && !(s.contractStart && addDays(s.contractStart, s.firstSurveyDueDays) >= start),
  );
  if (unplanned.length === 1) {
    const s = unplanned[0]!;
    return new Map([[s.siteId, addDays(start, s.firstSurveyDueDays)]]);
  }
  return staggerInitialDueDates(unplanned, start, cycleMonths);
}

export type NotificationKind = 'REMINDER' | 'ESCALATION';

export interface ScheduleNotification {
  kind: NotificationKind;
  /** Threshold that triggered it, e.g. 30 for "30 days before" or "30 days overdue". */
  threshold: number;
  /** Escalation level: 1 = branch manager, 2 = country manager, … */
  level: number;
}

/**
 * Which reminder/escalation is due today. Only the current threshold counts —
 * the nearest reminder crossed, or the furthest escalation crossed — and it
 * fires once (the caller records sent thresholds and passes them back in).
 * Thresholds it has superseded never fire later.
 */
export function dueNotifications(
  due: IsoDate,
  today: IsoDate,
  cfg: { reminderDaysBeforeDue: number[]; escalationDaysOverdue: number[]; escalationEmailsEnabled: boolean },
  alreadySent: { reminders: number[]; escalations: number[] },
): ScheduleNotification[] {
  const days = daysToFollowUp(due, today);
  if (days >= 0) {
    const crossed = cfg.reminderDaysBeforeDue.filter((t) => days <= t);
    if (crossed.length === 0) return [];
    const current = Math.min(...crossed);
    return alreadySent.reminders.includes(current) ? [] : [{ kind: 'REMINDER', threshold: current, level: 0 }];
  }
  if (!cfg.escalationEmailsEnabled) return [];
  const thresholds = [...cfg.escalationDaysOverdue].sort((a, b) => a - b);
  const crossed = thresholds.filter((t) => -days >= t);
  if (crossed.length === 0) return [];
  const current = crossed[crossed.length - 1]!;
  if (alreadySent.escalations.includes(current)) return [];
  return [{ kind: 'ESCALATION', threshold: current, level: Math.min(thresholds.indexOf(current) + 1, 2) }];
}
